/**
 * Claude Sonnet 5.5 (like Sonnet 5 before it) uses Anthropic's high-resolution
 * tier: 2576 px on the long edge and at most 4784 visual tokens (28×28-pixel
 * patches). Both limits apply at once; the visual-token limit binds for typical
 * photo aspect ratios.
 *
 * Standard-tier models such as Sonnet 4.6 use 1568 px / 1568 visual tokens.
 * If CLAUDE_MODEL moves down to that tier, lower these values too; otherwise
 * Anthropic will perform a second server-side resize after this client resize.
 */
export const MAX_IMAGE_EDGE_PX = 2576;
export const MAX_IMAGE_VISUAL_TOKENS = 4784;
export const IMAGE_PATCH_SIZE_PX = 28;

const RESIZABLE_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export type DownscaleResult = {
  file: File;
  wasResized: boolean;
  originalDimensions: { width: number; height: number } | null;
  finalDimensions: { width: number; height: number } | null;
};

export type DownscaleImageOptions = {
  maxEdge?: number;
  maxVisualTokens?: number;
  quality?: number;
};

/**
 * Target size after fitting both edge and visual-token limits, or `null` when no
 * resize is needed (already within both limits, or invalid dimensions).
 *
 * `maxVisualTokens` defaults to infinity so the converted-image preview caller
 * keeps its independent longest-edge-only behavior.
 */
export const computeTargetDimensions = (
  width: number,
  height: number,
  maxEdge: number,
  maxVisualTokens: number = Number.POSITIVE_INFINITY,
): { width: number; height: number } | null => {
  if (
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    !Number.isFinite(maxEdge) ||
    (!Number.isFinite(maxVisualTokens) && maxVisualTokens !== Number.POSITIVE_INFINITY) ||
    width <= 0 ||
    height <= 0 ||
    maxEdge < 1 ||
    maxVisualTokens < 1
  ) {
    return null;
  }

  const dimensionsAt = (scale: number) => ({
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  });
  const fits = (dimensions: { width: number; height: number }) => {
    const visualTokens =
      Math.ceil(dimensions.width / IMAGE_PATCH_SIZE_PX) *
      Math.ceil(dimensions.height / IMAGE_PATCH_SIZE_PX);
    return (
      Math.max(dimensions.width, dimensions.height) <= maxEdge &&
      visualTokens <= maxVisualTokens
    );
  };

  if (fits({ width, height })) return null;

  // Patch rounding is stepwise, so a simple area scale can overshoot the token
  // cap. Binary-search the largest aspect-preserving integer size that fits.
  let low = 0;
  let high = 1;
  for (let i = 0; i < 60; i += 1) {
    const middle = (low + high) / 2;
    if (fits(dimensionsAt(middle))) low = middle;
    else high = middle;
  }
  return dimensionsAt(low);
};

function mimeFromFile(file: File): string {
  return file.type.split(";")[0].trim().toLowerCase();
}

function filenameForMime(originalName: string, mime: string): string {
  const base = originalName.replace(/\.[^.]+$/, "") || originalName;
  if (mime === "image/jpeg") return `${base}.jpg`;
  if (mime === "image/webp") return `${base}.webp`;
  if (mime === "image/png") return `${base}.png`;
  return originalName;
}

type DecodedImage = {
  width: number;
  height: number;
  draw: (ctx: CanvasRenderingContext2D, width: number, height: number) => void;
  close: () => void;
};

async function decodeImage(file: File): Promise<DecodedImage> {
  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file);
    return {
      width: bitmap.width,
      height: bitmap.height,
      draw: (ctx, width, height) => {
        ctx.drawImage(bitmap, 0, 0, width, height);
      },
      close: () => bitmap.close(),
    };
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("image decode failed"));
      el.src = objectUrl;
    });
    return {
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
      draw: (ctx, width, height) => {
        ctx.drawImage(img, 0, 0, width, height);
      },
      close: () => {
        URL.revokeObjectURL(objectUrl);
      },
    };
  } catch (err) {
    URL.revokeObjectURL(objectUrl);
    throw err;
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mime, quality);
  });
}

/**
 * Downscale a dashboard chat-upload image to the native edge/area limits.
 * Never throws: failures return the original file so the upload still proceeds.
 *
 * PNG is re-encoded as JPEG with a white backdrop. Timesheet photos (the hot
 * path) have no useful alpha; detecting a real alpha channel reliably in the
 * browser is more complexity than the extra bytes we would save by keeping PNG.
 */
export const downscaleImageFile = async (
  file: File,
  options?: DownscaleImageOptions,
): Promise<DownscaleResult> => {
  const mime = mimeFromFile(file);
  if (!mime.startsWith("image/") || !RESIZABLE_IMAGE_TYPES.has(mime)) {
    return { file, wasResized: false, originalDimensions: null, finalDimensions: null };
  }

  const maxEdge = options?.maxEdge ?? MAX_IMAGE_EDGE_PX;
  const maxVisualTokens = options?.maxVisualTokens ?? MAX_IMAGE_VISUAL_TOKENS;
  const quality = options?.quality ?? 0.85;

  let decoded: DecodedImage | null = null;
  let canvas: HTMLCanvasElement | null = null;
  try {
    decoded = await decodeImage(file);
    const originalDimensions = { width: decoded.width, height: decoded.height };
    const target = computeTargetDimensions(
      decoded.width,
      decoded.height,
      maxEdge,
      maxVisualTokens,
    );
    if (!target) {
      return { file, wasResized: false, originalDimensions, finalDimensions: originalDimensions };
    }

    canvas = document.createElement("canvas");
    canvas.width = target.width;
    canvas.height = target.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      console.warn("[downscaleImage] canvas 2d context unavailable");
      return { file, wasResized: false, originalDimensions, finalDimensions: originalDimensions };
    }

    const outMime = mime === "image/webp" ? "image/webp" : "image/jpeg";
    if (outMime === "image/jpeg") {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, target.width, target.height);
    }
    decoded.draw(ctx, target.width, target.height);

    const blob = await canvasToBlob(canvas, outMime, quality);
    if (!blob) {
      console.warn("[downscaleImage] toBlob returned null");
      return { file, wasResized: false, originalDimensions, finalDimensions: originalDimensions };
    }

    const nextFile = new File([blob], filenameForMime(file.name, outMime), {
      type: outMime,
      lastModified: file.lastModified,
    });
    return {
      file: nextFile,
      wasResized: true,
      originalDimensions,
      finalDimensions: target,
    };
  } catch (err) {
    console.warn("[downscaleImage] skipped, using original file:", err);
    return { file, wasResized: false, originalDimensions: null, finalDimensions: null };
  } finally {
    decoded?.close();
    canvas = null;
  }
};
